import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, FileText, Upload, AlertTriangle } from 'lucide-react';
import { postDocumentFile } from '@/api/applicant';
import { Card } from '@/components/ui/Card';
import { Banner, QueryState } from '@/components/kit/ui';
import { errMsg } from '@/components/kit/data';
import { DOC_TYPES, NeedApplication, appKeys, docLabel, useApplication, useStatus } from '../shared';
import { Body, Hero, Screen } from '../ui';

const MAX = 5 * 1024 * 1024;

export function DocumentsPage() {
  const qc = useQueryClient();
  const q = useApplication();
  const status = useStatus();
  const app = q.data?.application ?? null;
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [busyType, setBusyType] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [pickType, setPickType] = useState<string>('');

  const upload = useMutation({
    mutationFn: ({ type, file }: { type: string; file: File }) => postDocumentFile(type, file),
    onMutate: ({ type }) => setBusyType(type),
    onSuccess: (d) => { setMsg({ tone: 'success', text: `${label(d.type)} uploaded.` }); qc.invalidateQueries({ queryKey: appKeys.app }); qc.invalidateQueries({ queryKey: appKeys.status }); },
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
    onSettled: () => setBusyType(null),
  });

  const required = app?.required_documents ?? app?.cycle?.required_documents ?? [];
  const label = (t: string) => required.find((r) => r.type === t)?.label ?? docLabel(t);
  const docs = app?.documents ?? [];
  const requested = (status.data?.document_requests ?? []).filter((r) => r.outstanding);
  const list = [...required, ...requested.filter((r) => !required.some((x) => x.type === r.type)).map((r) => ({ type: r.type, label: docLabel(r.type), required: true }))];
  const others = DOC_TYPES.filter((d) => !list.some((x) => x.type === d.value));

  const choose = (type: string) => { setPickType(type); setMsg(null); picker.current?.click(); };
  const onFile = (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX) { setMsg({ tone: 'error', text: 'Each file must be 5 MB or smaller.' }); return; }
    if (!/\.(jpe?g|png|webp|pdf)$/i.test(f.name)) { setMsg({ tone: 'error', text: 'Upload a JPG, PNG or PDF file.' }); return; }
    upload.mutate({ type: pickType, file: f });
  };

  return (
    <Screen>
      <Hero title={requested.length ? 'One more document' : 'Documents'} sub="Upload clear photos or PDFs (max 5 MB each). You can use your phone camera." />
      <Body>
      {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}
      <input ref={picker} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" capture={undefined} className="hidden" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
      <QueryState q={q}>
        {!app ? <NeedApplication>Save your application form before uploading documents.</NeedApplication> : (
          <>
            {requested.map((r) => (
              <Banner key={r.type} tone="warn"><AlertTriangle className="w-4 h-4 inline mr-1" />The school needs your {docLabel(r.type)}{r.note ? `: ${r.note}` : '.'}</Banner>
            ))}
            <Card>
              <ul className="divide-y divide-slate-100">
                {list.map((r) => {
                  const d = docs.find((x) => x.type === r.type);
                  return (
                    <li key={r.type} className="flex items-center gap-3 py-3">
                      {d ? <CheckCircle2 className={`w-5 h-5 shrink-0 ${d.status === 'rejected' ? 'text-red-500' : 'text-green-600'}`} /> : <FileText className="w-5 h-5 text-slate-300 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">{r.label}</p>
                        <p className="text-xs text-slate-500">{d ? (d.status === 'verified' ? 'Checked by the school' : d.status === 'rejected' ? 'Rejected — upload a clearer copy' : 'Uploaded') : r.required ? 'Required' : 'Optional'}
                          {d && <> · <a href={d.url} target="_blank" rel="noreferrer" className="text-brand-600 font-semibold">View</a></>}</p>
                      </div>
                      <button type="button" disabled={busyType === r.type || app.status === 'enrolled'} onClick={() => choose(r.type)}
                        className={`text-sm font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1 ${d ? 'bg-slate-100 text-slate-700' : 'bg-blue-50 text-blue-700'}`}>
                        <Upload className="w-3.5 h-3.5" />{busyType === r.type ? 'Uploading…' : d ? 'Replace' : 'Add'}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {others.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-slate-500">Something else?</span>
                  <select className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm" value="" onChange={(e) => e.target.value && choose(e.target.value)}>
                    <option value="">Upload another document…</option>
                    {others.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              )}
            </Card>
          </>
        )}
      </QueryState>
      </Body>
    </Screen>
  );
}
